import { UserRole } from '../types/roles'
import Token from '../api/auth-token-wrapper'

/**
 * User interface for role checking
 */
interface User {
  roles?: string[]
  realm_access?: {
    roles?: string[]
  }
}

/**
 * Parse JWT token to get user info and roles
 */
function parseJWTToken(token: string): any | null {
  try {
    // JWT tokens are in format header.payload.signature
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )

    return JSON.parse(jsonPayload)
  } catch (error) {
    console.error('Error parsing JWT token', error)
    return null
  }
}

/**
 * Get user from JWT token
 */
function getUserFromToken(): User | null {
  const token = Token.getToken()
  if (!token) return null

  const payload = parseJWTToken(token)
  if (!payload) return null

  return {
    roles: payload.roles || [],
    realm_access: payload.realm_access || { roles: [] },
  }
}

/**
 * Check if user has a specific role
 */
function hasUserRole(user: User | null | undefined, role: UserRole): boolean {
  if (!user) return false

  // Check in direct roles array
  if (user.roles && user.roles.includes(role)) {
    return true
  }

  // Check in realm_access.roles (Keycloak format)
  if (user.realm_access?.roles && user.realm_access.roles.includes(role)) {
    return true
  }

  return false
}

/**
 * Simple helper functions for common role checks
 */
export const roleUtils = {
  /**
   * Check if user has Site Manager role
   */
  isSiteManager: (user: User | null | undefined): boolean => {
    return hasUserRole(user, UserRole.SITE_MANAGER)
  },

  /**
   * Check if user has Fleet Manager role
   */
  isFleetManager: (user: User | null | undefined): boolean => {
    return hasUserRole(user, UserRole.FLEET_MANAGER)
  },

  /**
   * Check if user has any manager role (Site or Fleet)
   */
  isManager: (user: User | null | undefined): boolean => {
    return roleUtils.isSiteManager(user) || roleUtils.isFleetManager(user)
  },

  /**
   * Get user from current JWT token
   */
  getCurrentUser: (): User | null => {
    return getUserFromToken()
  },
}

/**
 * Hook for comprehensive role checking
 */
export const useRoleCheck = (user: User | null | undefined) => {
  return {
    hasSiteManagerRole: roleUtils.isSiteManager(user),
    hasFleetManagerRole: roleUtils.isFleetManager(user),
    hasManagerRole: roleUtils.isManager(user),
  }
}

export { hasUserRole, getUserFromToken, parseJWTToken }
export type { User }
